import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../../config';
import './AdminDashboard.css';
import AddUserModal from '../../admin/AddUserModal';
import EntryHistoryTable from '../../common/EntryHistoryTable';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user'));
  const [activeTab, setActiveTab] = useState('overview');
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [users, setUsers] = useState([]);
  const [inwardEntries, setInwardEntries] = useState([]);
  const [outwardEntries, setOutwardEntries] = useState([]);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalInward: 0,
    totalOutward: 0,
    monthInward: 0,
    monthOutward: 0,
    monthInwardAmount: 0,
    monthOutwardAmount: 0,
    mistakeEntries: 0 // <-- add mistake entries count
  });
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'User',
    location: ''
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [pagination, setPagination] = useState({
    users: { page: 1, itemsPerPage: 10 },
    inward: { page: 1, itemsPerPage: 10 },
    outward: { page: 1, itemsPerPage: 10 }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      // Fetch all data in parallel
      const [usersRes, inwardRes, outwardRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/users`, { headers }),
        axios.get(`${API_BASE_URL}/api/inward-entries`, { headers }),
        axios.get(`${API_BASE_URL}/api/outward-entries`, { headers })
      ]);

      setUsers(usersRes.data);

      // Transform inward entries data
      const transformedInwardEntries = inwardRes.data.map(entry => ({
        ...entry,
        materials: Array.isArray(entry.materials_list) ? entry.materials_list : 
                  Array.isArray(entry.materials) ? entry.materials : []
      }));
      setInwardEntries(transformedInwardEntries);

      // Transform outward entries data
      const transformedOutwardEntries = outwardRes.data.map(entry => ({
        ...entry,
        materials: Array.isArray(entry.materials_list) ? entry.materials_list : 
                  Array.isArray(entry.materials) ? entry.materials : []
      }));
      setOutwardEntries(transformedOutwardEntries);

      // --- Add this month stats calculation ---
      const now = new Date();
      const thisMonth = now.getMonth();
      const thisYear = now.getFullYear();

      const isThisMonth = (dateStr) => {
        if (!dateStr) return false;
        const d = new Date(dateStr);
        return d.getMonth() === thisMonth && d.getFullYear() === thisYear;
      };

      const inwardMonthEntries = transformedInwardEntries.filter(e => isThisMonth(e.entry_date));
      const outwardMonthEntries = transformedOutwardEntries.filter(e => isThisMonth(e.entry_date));

      // Sum all bill_amounts from bills array for each entry
      const inwardMonthAmount = inwardMonthEntries.reduce((sum, e) => {
        if (Array.isArray(e.bills)) {
          return sum + e.bills.reduce((bSum, b) => bSum + (parseFloat(b.bill_amount) || 0), 0);
        }
        return sum;
      }, 0);
      const outwardMonthAmount = outwardMonthEntries.reduce((sum, e) => {
        if (Array.isArray(e.bills)) {
          return sum + e.bills.reduce((bSum, b) => bSum + (parseFloat(b.bill_amount) || 0), 0);
        }
        return sum;
      }, 0);

      // --- Mistake entries: any entry with supervisor_action ---
      const hasSupervisorAction = (entry) => {
        if (!entry) return false;
        if (Array.isArray(entry.supervisor_action)) {
          return entry.supervisor_action.length > 0;
        }
        return !!entry.supervisor_action;
      };
      const mistakeEntriesCount =
        transformedInwardEntries.filter(hasSupervisorAction).length +
        transformedOutwardEntries.filter(hasSupervisorAction).length;

      setStats({
        totalUsers: usersRes.data.length,
        totalInward: transformedInwardEntries.length,
        totalOutward: transformedOutwardEntries.length,
        monthInward: inwardMonthEntries.length,
        monthOutward: outwardMonthEntries.length,
        monthInwardAmount: inwardMonthAmount,
        monthOutwardAmount: outwardMonthAmount,
        mistakeEntries: mistakeEntriesCount // <-- set mistake entries count
      });
      // --- end month stats ---

    } catch (err) {
      setError(err.response?.data?.message || 'Error fetching data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewUser(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE_URL}/api/users`, newUser, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setSuccess('User added successfully!');
      setError('');
      setNewUser({
        name: '',
        email: '',
        password: '',
        role: 'User',
        location: ''
      });
      fetchData(); // Refresh all data
      setTimeout(() => setSuccess(''), 3000);
      setShowAddUserModal(false); // Close modal after success
    } catch (err) {
      setError(err.response?.data?.message || 'Error adding user');
      setSuccess('');
    }
  };

  const handleToggleUserStatus = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_BASE_URL}/api/users/${userId}/toggle-status`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      fetchData();
      setSuccess('User status updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Error updating user status');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_BASE_URL}/api/users/${selectedUser.id}/change-password`,
        { newPassword },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setShowPasswordModal(false);
      setNewPassword('');
      setSelectedUser(null);
      setSuccess('Password changed successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Error changing password');
    }
  };

  const handlePageChange = (type, newPage) => {
    setPagination(prev => ({
      ...prev,
      [type]: { ...prev[type], page: newPage }
    }));
  };

  const getPaginatedData = (data, type) => {
    const { page, itemsPerPage } = pagination[type];
    const start = (page - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return data.slice(start, end);
  };

  const getPageCount = (data, type) => {
    return Math.ceil(data.length / pagination[type].itemsPerPage);
  };

  function formatDate(date) {
    if (!date) return '-';
    const d = new Date(date);
    return d.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' });
  }

  // Format date and time for last login
  function formatDateTime(date) {
    if (!date) return '-';
    const d = new Date(date);
    // Format: DD/MM/YYYY HH:mm:ss IST
    return d.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) + ' ' +
           d.toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour12: false });
  }

  const convertToCSV = (data) => {
    if (!data || data.length === 0) return '';

    // Always include all fields for Admin export, but remove bill_number and bill_amount, add Bill Details
    let headers = Array.from(new Set(
      data.reduce((acc, obj) => [...acc, ...Object.keys(obj)], [])
    ));
    // Remove bill_number and bill_amount if present
    headers = headers.filter(h => h !== 'bill_number' && h !== 'bill_amount');
    // Remove Bill Details column if present
    headers = headers.filter(h => h !== 'Bill Details');

    const csvRows = [];
    csvRows.push(headers.join(','));

    for (const row of data) {
      const values = headers.map(header => {
        let value = row[header] || '';
        // Bill Details column
        if (header === 'Bill Details') {
          if (Array.isArray(row.bills) && row.bills.length > 0) {
            value = row.bills.map(b => `${b.bill_number || ''} (${b.bill_amount || ''})`).join('; ');
          } else {
            value = '';
          }
        }
        // Materials column
        if ((header === 'Materials' || header === 'materials') && Array.isArray(row.materials_list)) {
          value = row.materials_list
            .map(m => `${m.name || ''} (${m.quantity || ''} ${m.uom || ''})`)
            .join('; ');
        }
        // Date columns
        if (header === 'Date' && row.entry_date) {
          value = new Date(row.entry_date).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' });
        }
        // Format outdate/out_date as YYYY-MM-DD
        if ((header === 'outdate' || header === 'out_date') && value) {
          if (typeof value === 'string' && value.length >= 10) {
            value = value.slice(0, 10);
          } else if (value instanceof Date) {
            value = value.toISOString().slice(0, 10);
          } else if (value) {
            try {
              value = new Date(value).toISOString().slice(0, 10);
            } catch {}
          }
        }
        // Escape special characters
        if (typeof value === 'string') {
          value = value.replace(/"/g, '""');
          if (value.includes(',') || value.includes('\n') || value.includes('"')) {
            value = `"${value}"`;
          }
        }
        return value;
      });
      csvRows.push(values.join(','));
    }

    return csvRows.join('\n');
  };

  const handleExportInward = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/export/inward-entries`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.data.data || response.data.data.length === 0) {
        setError('No data to export');
        return;
      }

      // Convert to CSV
      const csvContent = convertToCSV(response.data.data);

      // Create Blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `inward_entries_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
      setError('Error exporting data');
    }
  };

  const handleExportOutward = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/api/export/outward-entries`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.data.data || response.data.data.length === 0) {
        setError('No data to export');
        return;
      }

      // Convert to CSV
      const csvContent = convertToCSV(response.data.data);

      // Create Blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `outward_entries_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
      setError('Error exporting data');
    }
  };

  const generatePaginationNumbers = (currentPage, totalPages) => {
    let pages = [];
    
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pages = [1, 2, 3, 4, 5, '...', totalPages];
      } else if (currentPage >= totalPages - 2) {
        pages = [1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
      } else {
        pages = [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
      }
    }
    return pages;
  };

  if (!user || user.role !== 'Admin') {
    navigate('/login');
    return null;
  }

  if (loading) {
    return (
      <div className="dashboard">
        <div className="loading-message">Loading data...</div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <nav className="dashboard-nav">
        <div className="nav-left">
          <h2>Admin Dashboard</h2>
        </div>
        <div className="nav-right">
          <div className="user-info">
            <span>Welcome, {user.name}</span>
            <button onClick={handleLogout} className="logout-button">
              <i className="fas fa-sign-out-alt"></i> Logout
            </button>
          </div>
        </div>
      </nav>

      <div className="dashboard-content">
        {/* Stats Overview */}
        <div className="stats-grid">
          {/* Add 'stat-card-rounded' class for rounded corners */}
          <div className="stat-card users stat-card-rounded">
            <div className="stat-icon">
              <i className="fas fa-users"></i>
            </div>
            <div className="stat-details">
              <h3>Total Users</h3>
              <p>{stats.totalUsers}</p>
            </div>
          </div>
          <div className="stat-card inward stat-card-rounded">
            <div className="stat-icon">
              <i className="fas fa-sign-in-alt"></i>
            </div>
            <div className="stat-details">
              <h3>Inward Entries</h3>
              <p>{stats.totalInward}</p>
            </div>
          </div>
          <div className="stat-card outward stat-card-rounded">
            <div className="stat-icon">
              <i className="fas fa-sign-out-alt"></i>
            </div>
            <div className="stat-details">
              <h3>Outward Entries</h3>
              <p>{stats.totalOutward}</p>
            </div>
          </div>
          {/* --- New: Mistake/Cancel/Wrong Vehicle Entries --- */}
          <div className="stat-card mistake stat-card-rounded">
            <div className="stat-icon">
              <i className="fas fa-exclamation-triangle" style={{ color: "#eab308" }}></i>
            </div>
            <div className="stat-details">
              <h3>Mistake Entries</h3>
              <p>{stats.mistakeEntries}</p>
            </div>
          </div>
          {/* --- New: This Month Inward --- */}
          <div className="stat-card inward stat-card-rounded">
            <div className="stat-icon">
              <i className="fas fa-calendar-plus"></i>
            </div>
            <div className="stat-details">
              <h3>This Month Inward</h3>
              <p>{stats.monthInward}</p>
              <div style={{ fontSize: '0.95em', color: '#16a34a', marginTop: 4 }}>
                ₹ {stats.monthInwardAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>
          {/* --- New: This Month Outward --- */}
          <div className="stat-card outward stat-card-rounded">
            <div className="stat-icon">
              <i className="fas fa-calendar-check"></i>
            </div>
            <div className="stat-details">
              <h3>This Month Outward</h3>
              <p>{stats.monthOutward}</p>
              <div style={{ fontSize: '0.95em', color: '#dc2626', marginTop: 4 }}>
                ₹ {stats.monthOutwardAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="admin-tabs">
          <button 
            className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button 
            className={`tab-button ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            Users
          </button>
          <button 
            className={`tab-button ${activeTab === 'inward' ? 'active' : ''}`}
            onClick={() => setActiveTab('inward')}
          >
            Inward Entries
          </button>
          <button 
            className={`tab-button ${activeTab === 'outward' ? 'active' : ''}`}
            onClick={() => setActiveTab('outward')}
          >
            Outward Entries
          </button>
        </div>

        {/* Tab Content */}
        <div className="tab-content">
          {activeTab === 'users' && (
            <div className="section">
              <div className="section-header">
                <h3 style={{ marginRight: 'auto' }}>User Management</h3>
                <button 
                  className="action-button"
                  onClick={() => setShowAddUserModal(true)}
                >
                  <i className="fas fa-plus"></i>
                  Add User
                </button>
              </div>
              <AddUserModal
                isOpen={showAddUserModal}
                onClose={() => {
                  setShowAddUserModal(false);
                  setError('');
                  setSuccess('');
                  setNewUser({
                    name: '',
                    email: '',
                    password: '',
                    role: 'User',
                    location: ''
                  });
                }}
                onSubmit={handleAddUser}
                newUser={newUser}
                handleInputChange={handleInputChange}
                error={error}
                success={success}
              />
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Location</th>
                      <th>Last Login</th>
                      <th>Last Updated</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getPaginatedData(users, 'users').map(user => (
                      <tr key={user.id}>
                        <td>{user.id}</td>
                        <td>{user.name}</td>
                        <td>{user.email}</td>
                        <td>
                          <span className={`role-badge ${user.role.toLowerCase()}`}>
                            {user.role}
                          </span>
                        </td>
                        <td>{user.location || '-'}</td>
                        <td>{formatDateTime(user.last_login)}</td>
                        <td>{formatDate(user.updated_at)}</td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className={`status-button ${user.is_disabled ? 'enable' : 'disable'}`}
                              onClick={() => handleToggleUserStatus(user.id)}
                            >
                              {user.is_disabled ? 'Enable' : 'Disable'}
                            </button>
                            <button
                              className="password-button"
                              onClick={() => {
                                setSelectedUser(user);
                                setShowPasswordModal(true);
                              }}
                            >
                              Change Password
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="pagination">
                  <button
                    className="pagination-button nav-button"
                    disabled={pagination.users.page === 1}
                    onClick={() => handlePageChange('users', pagination.users.page - 1)}
                  >
                    <i className="fas fa-chevron-left"></i>
                  </button>
                  
                  {generatePaginationNumbers(pagination.users.page, getPageCount(users, 'users')).map((pageNum, idx) => (
                    pageNum === '...' ? (
                      <span key={`ellipsis-${idx}`} className="pagination-ellipsis">...</span>
                    ) : (
                      <button
                        key={pageNum}
                        className={`pagination-button ${pagination.users.page === pageNum ? 'active' : ''}`}
                        onClick={() => handlePageChange('users', pageNum)}
                      >
                        {pageNum}
                      </button>
                    )
                  ))}

                  <button
                    className="pagination-button nav-button"
                    disabled={pagination.users.page === getPageCount(users, 'users')}
                    onClick={() => handlePageChange('users', pagination.users.page + 1)}
                  >
                    <i className="fas fa-chevron-right"></i>
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'inward' && (
            <div className="section">
              <div className="section-header">
                <h3 style={{ marginRight: 'auto' }}>Inward Entries</h3>
                <button 
                  className="action-button"
                  onClick={handleExportInward}
                >
                  <i className="fas fa-download"></i> Export to CSV
                </button>
              </div>
              <div className="table-container">
                <EntryHistoryTable
                  entries={inwardEntries}
                  type="inward"
                  showActions={false}
                  readOnly={true}
                  showCreatedBy={true}
                  showPONumber={true}
                />
              </div>
            </div>
          )}

          {activeTab === 'outward' && (
            <div className="section">
              <div className="section-header">
                <h3 style={{ marginRight: 'auto' }}>Outward Entries</h3>
                <button 
                  className="action-button"
                  onClick={handleExportOutward}
                >
                  <i className="fas fa-download"></i> Export to CSV
                </button>
              </div>
              <div className="table-container">
                <EntryHistoryTable
                  entries={outwardEntries}
                  type="outward"
                  showActions={false}
                  readOnly={true}
                  showCreatedBy={true}
                  showPONumber={true}
                />
              </div>
            </div>
          )}

          {activeTab === 'overview' && (
            <div className="section">
              <div className="section-header">
                <h3 style={{ marginRight: 'auto' }}>Recent Activity</h3>
              </div>
              <div className="activity-grid">
                <div className="activity-card">
                  <h4>Recent Users</h4>
                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Role</th>
                          <th>Last Login</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.slice(0, 5).map(user => (
                          <tr key={user.id}>
                            <td>{user.name}</td>
                            <td>
                              <span className={`role-badge ${user.role.toLowerCase()}`}>
                                {user.role}
                              </span>
                            </td>
                            <td>{formatDateTime(user.last_login)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="activity-card">
                  <h4>Recent Inward Entries</h4>
                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Serial No.</th>
                          <th>Party</th>
                          <th>Created By</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inwardEntries.slice(0, 5).map(entry => (
                          <tr key={entry.id}>
                            <td>{entry.serial_number}</td>
                            <td>{entry.party_name}</td>
                            <td>{entry.created_by_name}</td>
                            
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="activity-card">
                  <h4>Recent Outward Entries</h4>
                  <div className="table-container">
                    <table>
                      <thead>
                        <tr>
                          <th>Serial No.</th>
                          <th>Party</th>
                          <th>Created By</th>
                        </tr>
                      </thead>
                      <tbody>
                        {outwardEntries.slice(0, 5).map(entry => (
                          <tr key={entry.id}>
                            <td>{entry.serial_number}</td>
                            <td>{entry.party_name}</td>
                            <td>{entry.created_by_name}</td>
                            
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Password Change Modal */}
      {showPasswordModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Change Password for {selectedUser?.name}</h3>
            <form onSubmit={handleChangePassword}>
              <div className="form-group">
                <label>New Password:</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength="6"
                />
              </div>
              <div className="modal-buttons">
                <button type="submit" className="submit-button">
                  Change Password
                </button>
                <button
                  type="button"
                  className="cancel-button"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setNewPassword('');
                    setSelectedUser(null);
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
